import { ChangeDetectionStrategy, Component, HostBinding, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest, filter, map } from 'rxjs';

import { DropdownOption, OptionType } from '../../../../../lib/dropdown/models';
import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../base/store/state';
import { SystemSlipType } from '../../../bet-generation/slip-type';
import { IBetslipInfoMessage } from '../../../common/models';
import { InfoMessagesActions } from '../../../info-messages/actions';
import BetslipSystemBetActions from '../../actions';
import { ISlipInfo, ISystemSlipTypeInfo } from '../../models';
import { systemBetBarStateSelector } from '../../selectors';
import SystemBetTypesService, { SystemTypeOrder } from '../../services/types/system-bet-types.service';

interface BetBarViewModel {
    isBankerActive: boolean;
    options: DropdownOption<ISlipInfo>[];
    selectedOption: DropdownOption<ISlipInfo>;
}

@Component({
    selector: 'bs-digital-system-bet-bar',
    templateUrl: './system-bet-bar.component.html',
    styleUrls: ['./system-bet-bar.component.scss'],
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export default class SystemBetBarComponent implements OnInit, OnDestroy {
    @HostBinding('class.bs-digital-system-bet-bar') hostCssClass = true;

    viewModel$: Observable<BetBarViewModel>;

    selectSubscription: Subscription;

    messages: IBetslipMessages;
    betslipInfoMessage: IBetslipInfoMessage;
    readonly bankerHelpMessageId = 'bankerHelpDialog';

    constructor(
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private store: Store<IBetslipRootState>,
        private typesService: SystemBetTypesService,
    ) {
        this.messages = messageProvider.getMessages();
        this.betslipInfoMessage = {
            header: this.messages.common.BankerHelpHeader,
            infoText: this.messages.common.BankerHelpText,
            closeText: this.messages.common.BankerHelpClose,
        };
    }

    ngOnInit(): void {
        const types$ = this.typesService.getAvailableSystemTypes$(SystemTypeOrder.ByIndex);
        const betBarState$ = this.store.select(systemBetBarStateSelector).pipe(filter((systemBetBarInfo) => !!systemBetBarInfo));

        const combined$ = combineLatest([betBarState$, types$]);

        this.viewModel$ = combined$.pipe(
            map(([systemBetBarInfo, types]) => {
                const selectedTypeKey = systemBetBarInfo.selectedSlipType;

                const options = this.mapSlipTypesToOptions(types);

                const selectedOption = options.find((option) => option.id === selectedTypeKey) ?? options[0];

                return {
                    isBankerActive: systemBetBarInfo.isBankerActive,
                    options,
                    selectedOption,
                };
            }),
        );

        this.selectSubscription = combined$.subscribe(([betBarState, types]) => {
            if (types.length === 0) {
                return;
            }

            const selectedOption = types.find((type) => type.slipType.key === betBarState.selectedSlipType);

            if (selectedOption) {
                return;
            }

            this.updateSystemType(types[0].slipType);
        });
    }

    ngOnDestroy(): void {
        this.selectSubscription.unsubscribe();
    }

    getBetCount = (betCount: number) => {
        return interpolateDoubleBracesString(this.messages.common.NumberOfSystemBets, { bets: betCount });
    };

    onOptionSelected(option: DropdownOption<ISlipInfo>): void {
        if (option.id === '-1') {
            return;
        }

        this.updateSystemType(option.payload!.slipType);
    }

    private updateSystemType(systemType: SystemSlipType) {
        this.store.dispatch(
            BetslipSystemBetActions.setSystemType({
                systemType,
            }),
        );
    }

    toggleBanker(): void {
        this.store.dispatch(BetslipSystemBetActions.toggleSystemBanker());
    }

    toggleBankerHelp(): void {
        this.store.dispatch(InfoMessagesActions.toggle({ messageId: this.bankerHelpMessageId }));
    }

    private mapSlipTypesToOptions(slipTypes: ISystemSlipTypeInfo[]): DropdownOption<ISlipInfo>[] {
        if (slipTypes.length === 0) {
            return [
                {
                    id: '-1',
                    name: '-',
                    type: OptionType.Regular,
                },
            ];
        }

        return slipTypes.map((type) => {
            return {
                id: type.slipType.key,
                name: type.typeName,
                type: OptionType.Regular,
                payload: {
                    slipType: type.slipType,
                    betCount: type.betCount,
                },
            };
        });
    }
}
